import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import "./submit_manufacturer.css";
import { useFormContext } from "../../../context/Form_context";
import { useBasicContext } from "../../../context/Basic_context";
import Notification from "../../../utils/Notification";
import Loader from "../../../Common/loader/Loader";
import {
  FaUser,
  FaBuilding,
  FaEnvelope,
  FaBriefcase,
  FaGlobe,
  FaLocationDot,
  FaCity,
  FaCircleCheck,
  FaArrowRight,
  FaStore,
  FaPhone,
  FaCalendarDays,
  FaGem,
  FaHeadset,
  FaShieldHalved,
  FaChevronDown,
} from "react-icons/fa6";

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const isFieldEnabled = (format, key) => {
  if (!format) return false;
  const val = format[key];
  return val === "1" || val === 1 || val === true || val === "true";
};

const getCleanOptions = (optionsArray) => {
  if (!optionsArray || !Array.isArray(optionsArray)) return [];
  return optionsArray
    .map((opt) =>
      typeof opt === "string"
        ? opt.trim()
        : opt?.option || opt?.name || String(opt).trim()
    )
    .filter((opt) => Boolean(opt) && opt !== "");
};

const isChildRequired = (child) => {
  if (!child) return false;
  return String(child.is_required) === "1" || child.is_required === 1;
};

const getChildOptions = (child) => {
  if (
    child?.visitor_format_child_option &&
    Array.isArray(child.visitor_format_child_option)
  ) {
    return child.visitor_format_child_option
      .filter((item) => {
        if (typeof item === "object" && item !== null) {
          const reqVal = item?.is_text_required ?? item?.is_required ?? item?.status;
          if (reqVal !== undefined && reqVal !== null) {
            return String(reqVal) === "1" || reqVal === 1 || reqVal === true || reqVal === "true";
          }
        }
        return true;
      })
      .map((item) => (typeof item === "object" ? item?.option || item?.name : item))
      .filter((opt) => Boolean(opt) && String(opt).trim() !== "");
  }
  return [];
};

const getFieldIcon = (label) => {
  const l = (label || "").toLowerCase();
  if (
    l.includes("website") ||
    l.includes("url") ||
    l.includes("link") ||
    l.includes("site")
  ) {
    return <FaGlobe className="exh-field-icon" />;
  }
  if (l.includes("mail")) {
    return <FaEnvelope className="exh-field-icon" />;
  }
  if (
    l.includes("company") ||
    l.includes("organization") ||
    l.includes("firm") ||
    l.includes("enterprise") ||
    l.includes("factory") ||
    l.includes("manufacturer")
  ) {
    return <FaBuilding className="exh-field-icon" />;
  }
  if (
    l.includes("phone") ||
    l.includes("mobile") ||
    l.includes("contact") ||
    l.includes("cell") ||
    l.includes("whatsapp")
  ) {
    return <FaPhone className="exh-field-icon" />;
  }
  if (l.includes("city")) {
    return <FaCity className="exh-field-icon" />;
  }
  if (
    l.includes("country") ||
    l.includes("state") ||
    l.includes("address") ||
    l.includes("location")
  ) {
    return <FaLocationDot className="exh-field-icon" />;
  }
  if (
    l.includes("sector") ||
    l.includes("industry") ||
    l.includes("business") ||
    l.includes("category") ||
    l.includes("designation")
  ) {
    return <FaBriefcase className="exh-field-icon" />;
  }
  if (
    l.includes("date") ||
    l.includes("dob") ||
    l.includes("birth") ||
    l.includes("calendar")
  ) {
    return <FaCalendarDays className="exh-field-icon" />;
  }
  return <FaUser className="exh-field-icon" />;
};

const SubmitManufacturer = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get("id");
  const stateId = location.state?.categoryId || location.state?.id;

  const {
    GetRegisterDetailExpo,
    GetRegisterForm,
    GetVisitorFormat,
    PostManufacturerExhibitorForm,
    register_form_data,
    register_form_loading,
    register_detail_expo_data,
    register_detail_expo_loading,
    visitor_format_data,
    submit_manufacturer_exhibitor_loading,
  } = useFormContext();

  const {
    GetCountry,
    get_country_data,
    get_country_loading,
    GetState,
    get_state_data,
    get_state_loading,
    GetCity,
    get_city_data,
    get_city_loading,
  } = useBasicContext();

  useEffect(() => {
    GetVisitorFormat("3");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFormat = useMemo(() => {
    return visitor_format_data || {};
  }, [visitor_format_data]);

  useEffect(() => {
    if (
      isFieldEnabled(activeFormat, "country") ||
      isFieldEnabled(activeFormat, "country_code") ||
      isFieldEnabled(activeFormat, "state") ||
      isFieldEnabled(activeFormat, "city")
    ) {
      GetCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormat]);

  useEffect(() => {
    const activeId = queryId || stateId;
    const isFormLoaded =
      register_form_data && Object.keys(register_form_data).length > 0;

    if (activeId) {
      GetRegisterDetailExpo(activeId);
    } else if (register_form_data?.registration_categories?.length > 0) {
      const categories = register_form_data.registration_categories;
      const matched = categories.find(
        (c) =>
          c.name?.toLowerCase().includes("manufacturer") ||
          c.tag?.toLowerCase().includes("manufacturer") ||
          c.name?.toLowerCase().includes("exhibitor") ||
          c.tag?.toLowerCase().includes("exhibitor") ||
          c.name?.toLowerCase().includes("stall") ||
          c.name?.toLowerCase().includes("inquiry"),
      );

      if (matched?.id) {
        GetRegisterDetailExpo(matched.id);
      }
    } else if (!isFormLoaded && !register_form_loading) {
      GetRegisterForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId, stateId, register_form_data]);

  const formattedDates = useMemo(() => {
    const fromStr = register_detail_expo_data?.from_date;
    const toStr = register_detail_expo_data?.to_date;
    if (!fromStr || !toStr) return "";

    const fromDate = dayjs(fromStr);
    const toDate = dayjs(toStr);

    if (!fromDate.isValid() || !toDate.isValid()) return "";

    if (
      fromDate.month() === toDate.month() &&
      fromDate.year() === toDate.year()
    ) {
      return `${fromDate.format("D")} – ${toDate.format("D MMMM YYYY")}`;
    } else {
      return `${fromDate.format("D MMM")} – ${toDate.format("D MMM YYYY")}`;
    }
  }, [
    register_detail_expo_data?.from_date,
    register_detail_expo_data?.to_date,
  ]);

  const [formData, setFormData] = useState({
    isdCode: "+91",
    countryCode: "+91",
    title: "Mr.",
  });

  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedProductCategories, setSelectedProductCategories] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [formKey, setFormKey] = useState(0);

  const handleCategoryToggle = (categoryName) => {
    setSelectedProductCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((item) => item !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
    if (formErrors.selectedProductCategories || formErrors.productCategory) {
      setFormErrors((prev) => ({
        ...prev,
        selectedProductCategories: "",
        productCategory: "",
      }));
    }
  };

  const currentCountryObj = useMemo(() => {
    const targetCode = (formData.isdCode || formData.countryCode || "").trim();
    if (!targetCode || !Array.isArray(get_country_data)) return null;
    return get_country_data.find((c) => {
      const rawCode =
        c?.phonecode ||
        c?.phone_code ||
        c?.country_code ||
        c?.isd_code ||
        c?.code ||
        "";
      const formattedCode = String(rawCode).startsWith("+")
        ? String(rawCode)
        : `+${rawCode}`;
      return formattedCode === targetCode;
    });
  }, [formData.isdCode, formData.countryCode, get_country_data]);

  const selectedSortName = useMemo(() => {
    const sortName =
      currentCountryObj?.sortname ||
      currentCountryObj?.sort_name ||
      currentCountryObj?.iso2 ||
      currentCountryObj?.iso_code ||
      currentCountryObj?.short_name ||
      ((formData.isdCode === "+91" || formData.countryCode === "+91") ? "in" : "");
    return sortName ? sortName.toLowerCase() : "";
  }, [currentCountryObj, formData.isdCode, formData.countryCode]);

  useEffect(() => {
    if (formData.country) {
      GetState(formData.country);
    } else if (
      isFieldEnabled(activeFormat, "state") &&
      !isFieldEnabled(activeFormat, "country")
    ) {
      GetState(101);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country, activeFormat]);

  useEffect(() => {
    if (formData.state) {
      GetCity(formData.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.state]);

  const resetForm = () => {
    setFormData({
      isdCode: "+91",
      countryCode: "+91",
      title: "Mr.",
    });
    setSelectedSectors([]);
    setSelectedProductCategories([]);
    setFormErrors({});
    setFormKey((prev) => prev + 1);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
        if (value && Array.isArray(get_country_data)) {
          const matchedCountry = get_country_data.find(
            (c) =>
              c?.name === value ||
              c?.country_name === value ||
              String(c?.id) === String(value)
          );
          const rawCode =
            matchedCountry?.phonecode ||
            matchedCountry?.phone_code ||
            matchedCountry?.country_code ||
            matchedCountry?.isd_code ||
            matchedCountry?.code;
          if (rawCode) {
            const formattedCode = String(rawCode).startsWith("+")
              ? String(rawCode)
              : `+${rawCode}`;
            updated.isdCode = formattedCode;
            updated.countryCode = formattedCode;
          }
        }
      }
      if (name === "state") {
        updated.city = "";
      }
      if (name === "heardAbout") {
        const isOther =
          value?.trim().toLowerCase() === "other" ||
          value?.trim().toLowerCase().includes("other");
        if (!isOther) {
          updated.heardAboutOther = "";
        }
      }
      return updated;
    });

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "heardAbout" && formErrors.heardAboutOther) {
      setFormErrors((prev) => ({ ...prev, heardAboutOther: "" }));
    }
    if (name === "heardAboutOther" && formErrors.heardAboutOther) {
      setFormErrors((prev) => ({ ...prev, heardAboutOther: "" }));
    }
  };

  const handleSectorToggle = (sectorName) => {
    setSelectedSectors((prev) => {
      if (prev.includes(sectorName)) {
        return [];
      } else {
        return [sectorName];
      }
    });
    if (formErrors.selectedSectors) {
      setFormErrors((prev) => ({ ...prev, selectedSectors: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (
      (isFieldEnabled(activeFormat, "purpose_of_inq") ||
        isFieldEnabled(activeFormat, "purpose_of_inquiry")) &&
      !formData.purposeOfInq?.trim()
    ) {
      errors.purposeOfInq = "Select Purpose of Inquiry";
    }

    if (
      isFieldEnabled(activeFormat, "first_name") &&
      !formData.firstName?.trim()
    ) {
      errors.firstName = "Enter First Name";
    }
    if (
      isFieldEnabled(activeFormat, "last_name") &&
      !formData.lastName?.trim()
    ) {
      errors.lastName = "Enter Last Name";
    }
    if (
      (isFieldEnabled(activeFormat, "company") ||
        isFieldEnabled(activeFormat, "company_name")) &&
      !formData.companyName?.trim()
    ) {
      errors.companyName = "Enter Company Name";
    }
    if (
      (isFieldEnabled(activeFormat, "designation") ||
        isFieldEnabled(activeFormat, "designation_name")) &&
      !formData.designation?.trim()
    ) {
      errors.designation = "Enter Designation";
    }
    if (isFieldEnabled(activeFormat, "email")) {
      const emailVal = formData.email?.trim();
      if (!emailVal) {
        errors.email = "Enter Email Address";
      } else if (!/\S+@\S+\.\S+/.test(emailVal)) {
        errors.email = "Enter valid Email Address";
      }
    }
    if (
      isFieldEnabled(activeFormat, "confirm_email") ||
      isFieldEnabled(activeFormat, "confirm_email_address")
    ) {
      const confirmVal = formData.confirmEmail?.trim();
      if (!confirmVal) {
        errors.confirmEmail = "Enter Confirm Email Address";
      } else if (!/\S+@\S+\.\S+/.test(confirmVal)) {
        errors.confirmEmail = "Enter valid Confirm Email Address";
      } else if (
        formData.email?.trim() &&
        formData.email.trim().toLowerCase() !== confirmVal.toLowerCase()
      ) {
        errors.confirmEmail = "Email addresses do not match";
      }
    }
    if (
      isFieldEnabled(activeFormat, "company_website") ||
      isFieldEnabled(activeFormat, "website") ||
      isFieldEnabled(activeFormat, "website_url")
    ) {
      if (!formData.companyWebsite?.trim()) {
        errors.companyWebsite = "Enter Company Website";
      }
    }
    if (
      (isFieldEnabled(activeFormat, "contact") ||
        isFieldEnabled(activeFormat, "contact_no") ||
        isFieldEnabled(activeFormat, "mobile") ||
        isFieldEnabled(activeFormat, "whatsapp")) &&
      !formData.mobilePhone?.trim() &&
      !formData.contact?.trim()
    ) {
      errors.mobilePhone = "Enter Mobile Number";
    }
    if (isFieldEnabled(activeFormat, "country") && !formData.country) {
      errors.country = "Select Country";
    }
    if (isFieldEnabled(activeFormat, "state") && !formData.state) {
      errors.state = "Select State";
    }
    if (isFieldEnabled(activeFormat, "city") && !formData.city?.trim()) {
      errors.city = "Select or Enter City";
    }
    if (
      isFieldEnabled(activeFormat, "product_category") &&
      selectedProductCategories.length === 0 &&
      !formData.productCategory
    ) {
      errors.selectedProductCategories = "Select at least one Product Category";
    }
    if (
      isFieldEnabled(activeFormat, "business_sector") &&
      selectedSectors.length === 0 &&
      !formData.businessSector
    ) {
      errors.selectedSectors = "Please select a Business Sector";
    }
    if (
      (isFieldEnabled(activeFormat, "heard_about") ||
        isFieldEnabled(activeFormat, "how_did_you_hear"))
    ) {
      if (!formData.heardAbout?.trim()) {
        errors.heardAbout = "Select Where did you hear about us";
      } else if (
        (formData.heardAbout.trim().toLowerCase() === "other" ||
          formData.heardAbout.trim().toLowerCase().includes("other")) &&
        !formData.heardAboutOther?.trim()
      ) {
        errors.heardAboutOther = "Please specify where you heard about us";
      }
    }

    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child
        .filter((child) => child && isChildRequired(child))
        .forEach((child) => {
          const fieldKey = `child_${child.id}`;
          const val = formData[fieldKey];
          const isSelectType =
            child.type === "select" ||
            child.type === "dropdown" ||
            child.type === "radio" ||
            child.type === "checkbox" ||
            child.type === "file" ||
            child.type === "image" ||
            child.type === "date" ||
            child.type === "datetime";
          const actionText = isSelectType ? "Select" : "Enter";

          if (!val || (typeof val === "string" && !val.trim())) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          } else if (Array.isArray(val) && val.length === 0) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          }
        });
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    const errorList = Object.values(errors);
    if (errorList.length > 0) {
      Notification("error", "Missing Info!", errorList[0], 3);
      return;
    }

    // ---------- Build FormData as per API (Postman) ----------
    const fd = new FormData();

    // first_name & last_name
    if (formData.firstName?.trim()) {
      fd.append("first_name", formData.firstName.trim());
    } else if (formData.fullName?.trim()) {
      fd.append("first_name", formData.fullName.trim());
    }

    if (formData.lastName?.trim()) {
      fd.append("last_name", formData.lastName.trim());
    }

    // designation_options[]
    if (formData.designation?.trim()) {
      fd.append("designation_options[]", formData.designation.trim());
    }

    // email
    if (formData.email?.trim()) {
      fd.append("email", formData.email.trim());
    }

    // confirm_email
    if (formData.confirmEmail?.trim()) {
      fd.append("confirm_email", formData.confirmEmail.trim());
    }

    // contact
    const contactVal = (
      formData.mobilePhone ||
      formData.mobile ||
      formData.contact ||
      formData.whatsapp ||
      ""
    ).trim();
    if (contactVal) fd.append("contact", contactVal);

    // company
    if (formData.companyName?.trim()) {
      fd.append("company", formData.companyName.trim());
    }

    // company_website
    if (formData.companyWebsite?.trim()) {
      fd.append("company_website", formData.companyWebsite.trim());
    }

    // country (id)
    const selectedCountry = Array.isArray(get_country_data)
      ? get_country_data.find(
        (c) =>
          c?.name === formData.country ||
          String(c?.id) === String(formData.country)
      )
      : null;
    fd.append("country", selectedCountry?.id || formData.country || "101");

    // country_code
    fd.append("country_code", formData.isdCode || formData.countryCode || "+91");

    // state (id)
    if (formData.state) fd.append("state", formData.state);

    // city (id / name)
    const selectedCity = Array.isArray(get_city_data)
      ? get_city_data.find((ct) => ct?.name === formData.city)
      : null;
    if (selectedCity?.id || formData.city) {
      fd.append("city", selectedCity?.id || formData.city);
    }

    // business_sector_options[]
    if (selectedSectors && selectedSectors.length > 0) {
      selectedSectors.forEach((sec) =>
        fd.append("business_sector_options[]", sec)
      );
    } else if (formData.businessSector) {
      fd.append("business_sector_options[]", formData.businessSector);
    }

    // product_category_option[]
    if (selectedProductCategories && selectedProductCategories.length > 0) {
      selectedProductCategories.forEach((cat) => {
        fd.append("product_category_option[]", cat);
      });
    } else if (formData.productCategory) {
      fd.append("product_category_option[]", formData.productCategory);
    }

    // purpose_of_inq_options[]
    if (formData.purposeOfInq?.trim()) {
      fd.append("purpose_of_inq_options[]", formData.purposeOfInq.trim());
    }

    // heard_about_options[] & other_heard_abt
    if (formData.heardAbout?.trim()) {
      const isOther =
        formData.heardAbout.trim().toLowerCase() === "other" ||
        formData.heardAbout.trim().toLowerCase().includes("other");

      fd.append("heard_about_options[]", formData.heardAbout.trim());

      if (isOther && formData.heardAboutOther?.trim()) {
        fd.append("other_heard_abt", formData.heardAboutOther.trim());
      }
    }

    // child_answers[id]
    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child.filter(Boolean).forEach((child) => {
        const val = formData[`child_${child.id}`];
        if (val === undefined || val === null || val === "") return;

        if (Array.isArray(val)) {
          val.forEach((v) => fd.append(`child_answers[${child.id}][]`, v));
        } else {
          fd.append(`child_answers[${child.id}]`, val);
        }
      });
    }

    // Wait for API result -> clear form on success (Calls submit_manufacturer_exhibitor_url)
    const isSuccess = await PostManufacturerExhibitorForm(fd);
    if (isSuccess) {
      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const rawHeardAboutOpts = getCleanOptions(
    activeFormat?.heard_about_options ||
    activeFormat?.heard_about_option ||
    activeFormat?.how_did_you_hear_options ||
    activeFormat?.how_did_you_hear_option
  );

  const heardAboutOpts = useMemo(() => {
    if (!rawHeardAboutOpts || rawHeardAboutOpts.length === 0) return [];
    const hasOther = rawHeardAboutOpts.some((o) =>
      String(o).toLowerCase().includes("other")
    );
    if (!hasOther) {
      return [...rawHeardAboutOpts, "Other"];
    }
    return rawHeardAboutOpts;
  }, [rawHeardAboutOpts]);

  const isExhibitorData =
    !register_detail_expo_data?.registration_category ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("manufacturer") ||
    register_detail_expo_data?.registration_category?.tag
      ?.toLowerCase()
      .includes("manufacturer") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("exhibitor") ||
    register_detail_expo_data?.registration_category?.tag
      ?.toLowerCase()
      .includes("exhibitor") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("stall") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("inquiry");

  if (
    register_detail_expo_loading ||
    register_form_loading ||
    !register_detail_expo_data ||
    Object.keys(register_detail_expo_data).length === 0 ||
    !isExhibitorData
  ) {
    return <Loader fullScreen={true} text="Loading Manufacturer Exhibitor Inquiry..." />;
  }

  const showFirstName =
    isFieldEnabled(activeFormat, "first_name") ||
    isFieldEnabled(activeFormat, "firstname");
  const showLastName =
    isFieldEnabled(activeFormat, "last_name") ||
    isFieldEnabled(activeFormat, "lastname");
  const showFullName = isFieldEnabled(activeFormat, "full_name");
  const showContact =
    isFieldEnabled(activeFormat, "contact") ||
    isFieldEnabled(activeFormat, "contact_no") ||
    isFieldEnabled(activeFormat, "mobile") ||
    isFieldEnabled(activeFormat, "whatsapp");
  const showEmail = isFieldEnabled(activeFormat, "email");
  const showConfirmEmail =
    isFieldEnabled(activeFormat, "confirm_email") ||
    isFieldEnabled(activeFormat, "confirm_email_address");
  const showCompany =
    isFieldEnabled(activeFormat, "company") ||
    isFieldEnabled(activeFormat, "company_name");
  const showCompanyWebsite =
    isFieldEnabled(activeFormat, "company_website") ||
    isFieldEnabled(activeFormat, "website") ||
    isFieldEnabled(activeFormat, "website_url");
  const showDesignation =
    isFieldEnabled(activeFormat, "designation") ||
    isFieldEnabled(activeFormat, "designation_name");
  const showProductCategory = isFieldEnabled(activeFormat, "product_category");
  const showBusinessSector = isFieldEnabled(activeFormat, "business_sector");
  const showPurposeOfInq =
    isFieldEnabled(activeFormat, "purpose_of_inq") ||
    isFieldEnabled(activeFormat, "purpose_of_inquiry");
  const showHeardAbout =
    isFieldEnabled(activeFormat, "heard_about") ||
    isFieldEnabled(activeFormat, "how_did_you_hear");
  const showCountryCode = isFieldEnabled(activeFormat, "country_code");
  const showCountry = isFieldEnabled(activeFormat, "country");
  const showState = isFieldEnabled(activeFormat, "state");
  const showCity = isFieldEnabled(activeFormat, "city");

  const designationOpts = getCleanOptions(
    activeFormat.designation_options || activeFormat.designation_option
  );

  const businessSectorOpts = getCleanOptions(
    activeFormat.business_sector_option ||
    activeFormat.business_sector_options
  );

  const productCategoryOpts = getCleanOptions(
    activeFormat.product_category_option ||
    activeFormat.product_category_options
  );

  const purposeOfInqOpts = getCleanOptions(
    activeFormat.purpose_of_inq_options ||
    activeFormat.purpose_of_inq_option ||
    activeFormat.purpose_of_inquiry_options ||
    activeFormat.purpose_of_inquiry_option
  );

  return (
    <div className="exh-inquiry-page">
      <Helmet>
        <title>Manufacturer Exhibitor Registration | Bharat Buildcon</title>
      </Helmet>
      {/* Decorative Background Glow Orbs */}
      <div className="exh-bg-glow exh-bg-glow--saffron" />
      <div className="exh-bg-glow exh-bg-glow--blue" />

      {/* ---------------- BREADCRUMB & HERO BANNER ---------------- */}
      <section className="exh-hero-section">
        <div className="exh-container">
          {/* DYNAMIC HIGH-IMPACT HERO BANNER CARD */}
          <div className="exh-hero-banner-card">
            <div className="exh-hero-bg-media">
              <img
                src={
                  register_detail_expo_data?.registration_detail
                    ?.image_full_path ||
                  "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1600&q=80"
                }
                alt="Bharat Buildcon Expo Hall"
                className="exh-hero-img"
              />
              <div className="exh-hero-overlay" />
              <div className="exh-hero-scan-line" />
              <div className="exh-hero-grid-pattern" />
            </div>

            <div className="exh-hero-content">
              <div className="exh-ribbon-badge">
                <span className="exh-ribbon-spark" />
                <FaStore className="exh-ribbon-icon" />
                <span>
                  {register_detail_expo_data?.registration_detail
                    ?.reg_detail_sub_heading || "MANUFACTURER EXHIBITOR'S REGISTRATION 2026"}
                </span>
              </div>

              <h1 className="exh-hero-title">
                {register_detail_expo_data?.registration_detail
                  ?.reg_detail_heading ? (
                  (() => {
                    const words =
                      register_detail_expo_data?.registration_detail?.reg_detail_heading
                        .trim()
                        .split(" ");
                    if (words.length === 1) {
                      return (
                        <span className="exh-shimmer-text">{words[0]}</span>
                      );
                    }
                    const mainText = words.slice(0, -1).join(" ");
                    const lastWord = words[words.length - 1];
                    return (
                      <>
                        {mainText}{" "}
                        <span className="exh-shimmer-text">{lastWord}</span>
                      </>
                    );
                  })()
                ) : (
                  <>
                    Manufacturer Exhibitors{" "}
                    <span className="exh-shimmer-text">Stall Inquiry</span>
                  </>
                )}
              </h1>
            </div>

            {/* In-Card Info Strip Bar */}
            <div className="exh-info-strip">
              {Number(
                register_detail_expo_data?.registration_category
                  ?.registration_free
              ) === 1 && (
                  <>
                    <div className="exh-info-item">
                      <div className="exh-info-icon-box">
                        <FaStore />
                      </div>
                      <div className="exh-info-text">
                        <span className="exh-info-label">ENTRY PASS</span>
                        <span className="exh-info-val">Free Entry Pass</span>
                      </div>
                    </div>

                    <div className="exh-info-divider" />
                  </>
                )}

              <div className="exh-info-item">
                <div className="exh-info-icon-box">
                  <FaCalendarDays />
                </div>
                <div className="exh-info-text">
                  <span className="exh-info-label">EVENT DATES</span>
                  <span className="exh-info-val">{formattedDates || ""}</span>
                </div>
              </div>

              <div className="exh-info-divider" />

              <div className="exh-info-item">
                <div className="exh-info-icon-box">
                  <FaLocationDot />
                </div>
                <div className="exh-info-text">
                  <span className="exh-info-label">VENUE</span>
                  <span className="exh-info-val">
                    {register_detail_expo_data?.venue_name || ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MAIN FORM SECTION ---------------- */}
      <section className="exh-main-section">
        <div className="exh-container">
          {/* ---------- FORM CONTAINER WITH SIDEBAR ---------- */}
          <Reveal>
            <div className="exh-grid-layout">
              <div className="exh-form-col">
                <div className="exh-form-card">
                  <div className="exh-form-header">
                    <span className="exh-form-subheading">
                      TELL US A LITTLE MORE ABOUT YOU
                    </span>
                    <h2 className="exh-form-title">
                      Manufacturer Exhibitor Registration &amp; Stall Details
                    </h2>
                    <p className="exh-form-desc">
                      Please fill out the form below to receive stall
                      availability, layout plans, and booth pricing options.
                    </p>
                  </div>

                  <form
                    key={formKey}
                    onSubmit={handleSubmit}
                    className="exh-form-body"
                    noValidate
                  >
                    {/* --- SECTION 1: PERSONAL & ORGANIZATION DETAILS --- */}
                    <div className="exh-form-block">
                      <div className="exh-form-grid">
                        {/* 1. Purpose of Inquiry */}
                        {showPurposeOfInq && (
                          <div className="exh-field-group">
                            <label htmlFor="purposeOfInq" className="exh-label">
                              Purpose of Inquiry <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaBriefcase className="exh-field-icon" />
                              {purposeOfInqOpts.length > 0 ? (
                                <select
                                  id="purposeOfInq"
                                  name="purposeOfInq"
                                  value={formData.purposeOfInq || ""}
                                  onChange={handleInputChange}
                                  className="exh-select"
                                >
                                  <option value="">Choose Purpose of Inquiry</option>
                                  {purposeOfInqOpts.map((opt, idx) => (
                                    <option key={idx} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  id="purposeOfInq"
                                  name="purposeOfInq"
                                  value={formData.purposeOfInq || ""}
                                  onChange={handleInputChange}
                                  placeholder="Enter purpose of inquiry"
                                  className="exh-input"
                                />
                              )}
                            </div>
                            {formErrors.purposeOfInq && (
                              <span className="exh-error-msg">
                                {formErrors.purposeOfInq}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 2. First Name */}
                        {showFirstName && (
                          <div className="exh-field-group">
                            <label htmlFor="firstName" className="exh-label">
                              First Name <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaUser className="exh-field-icon" />
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName || ""}
                                onChange={handleInputChange}
                                placeholder="Enter first name"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.firstName && (
                              <span className="exh-error-msg">
                                {formErrors.firstName}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 3. Last Name */}
                        {showLastName && (
                          <div className="exh-field-group">
                            <label htmlFor="lastName" className="exh-label">
                              Last Name <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaUser className="exh-field-icon" />
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName || ""}
                                onChange={handleInputChange}
                                placeholder="Enter last name"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.lastName && (
                              <span className="exh-error-msg">
                                {formErrors.lastName}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Full Name */}
                        {showFullName && (
                          <div className="exh-field-group">
                            <label htmlFor="fullName" className="exh-label">
                              Full Name <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaUser className="exh-field-icon" />
                              <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName || ""}
                                onChange={handleInputChange}
                                placeholder="Enter full name"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.fullName && (
                              <span className="exh-error-msg">
                                {formErrors.fullName}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 4. Company Name */}
                        {showCompany && (
                          <div className="exh-field-group">
                            <label htmlFor="companyName" className="exh-label">
                              Company <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaBuilding className="exh-field-icon" />
                              <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName || ""}
                                onChange={handleInputChange}
                                placeholder="Enter company name"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.companyName && (
                              <span className="exh-error-msg">
                                {formErrors.companyName}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 5. Company Website */}
                        {showCompanyWebsite && (
                          <div className="exh-field-group">
                            <label htmlFor="companyWebsite" className="exh-label">
                              Company Website
                            </label>
                            <div className="exh-input-wrapper">
                              <FaGlobe className="exh-field-icon" />
                              <input
                                type="url"
                                id="companyWebsite"
                                name="companyWebsite"
                                value={formData.companyWebsite || ""}
                                onChange={handleInputChange}
                                placeholder="https://www.example.com"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.companyWebsite && (
                              <span className="exh-error-msg">
                                {formErrors.companyWebsite}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 6. Designation */}
                        {showDesignation && (
                          <div className="exh-field-group">
                            <label htmlFor="designation" className="exh-label">
                              Designation <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaBriefcase className="exh-field-icon" />
                              {designationOpts.length > 0 ? (
                                <select
                                  id="designation"
                                  name="designation"
                                  value={formData.designation || ""}
                                  onChange={handleInputChange}
                                  className="exh-select"
                                >
                                  <option value="">Choose Designation</option>
                                  {designationOpts.map((desg, idx) => (
                                    <option key={idx} value={desg}>
                                      {desg}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  id="designation"
                                  name="designation"
                                  value={formData.designation || ""}
                                  onChange={handleInputChange}
                                  placeholder="Enter designation"
                                  className="exh-input"
                                />
                              )}
                            </div>
                            {formErrors.designation && (
                              <span className="exh-error-msg">
                                {formErrors.designation}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 7. Email Address */}
                        {showEmail && (
                          <div className="exh-field-group">
                            <label htmlFor="email" className="exh-label">
                              Email Address{" "}
                              <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaEnvelope className="exh-field-icon" />
                              <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email || ""}
                                onChange={handleInputChange}
                                placeholder="Enter email address"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.email && (
                              <span className="exh-error-msg">
                                {formErrors.email}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 8. Confirm Email Address */}
                        {showConfirmEmail && (
                          <div className="exh-field-group">
                            <label htmlFor="confirmEmail" className="exh-label">
                              Confirm Email Address{" "}
                              <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaEnvelope className="exh-field-icon" />
                              <input
                                type="email"
                                id="confirmEmail"
                                name="confirmEmail"
                                value={formData.confirmEmail || ""}
                                onChange={handleInputChange}
                                placeholder="Re-enter email address"
                                className="exh-input"
                              />
                            </div>
                            {formErrors.confirmEmail && (
                              <span className="exh-error-msg">
                                {formErrors.confirmEmail}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 9. Mobile Phone */}
                        {(showContact || showCountryCode) && (
                          <div className="exh-field-group">
                            <label htmlFor="mobilePhone" className="exh-label">
                              Mobile Phone <span className="exh-required">*</span>
                            </label>
                            <div className="exh-unified-phone-container">
                              {showCountryCode && (
                                <>
                                  <div className="exh-country-code-embed">
                                    {selectedSortName ? (
                                      <img
                                        src={`https://flagcdn.com/w40/${selectedSortName}.png`}
                                        alt={selectedSortName}
                                        className="exh-embed-flag-img"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <FaGlobe className="exh-embed-icon" />
                                    )}
                                    <span className="exh-embed-code-text">
                                      {formData.isdCode || formData.countryCode || "+91"}
                                    </span>
                                    <FaChevronDown className="exh-embed-chevron" />
                                    <select
                                      id="isdCode"
                                      name="isdCode"
                                      value={formData.isdCode || formData.countryCode || "+91"}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setFormData((prev) => ({
                                          ...prev,
                                          isdCode: val,
                                          countryCode: val,
                                        }));
                                      }}
                                      className="exh-embed-cc-select"
                                      disabled={get_country_loading}
                                    >
                                      {Array.isArray(get_country_data) &&
                                        get_country_data.map((c, idx) => {
                                          const rawCode =
                                            c?.phonecode ||
                                            c?.phone_code ||
                                            c?.country_code ||
                                            c?.isd_code ||
                                            c?.code ||
                                            "";
                                          if (!rawCode) return null;
                                          const formattedCode = String(rawCode).startsWith("+")
                                            ? String(rawCode)
                                            : `+${rawCode}`;
                                          const countryName = c?.name || c?.country_name || "";
                                          const displayLabel = countryName
                                            ? `${formattedCode} (${countryName})`
                                            : formattedCode;
                                          return (
                                            <option key={c?.id || idx} value={formattedCode}>
                                              {displayLabel}
                                            </option>
                                          );
                                        })}
                                    </select>
                                  </div>
                                  <div className="exh-phone-divider" />
                                </>
                              )}

                              <div className="exh-mobile-number-embed">
                                <FaPhone className="exh-embed-icon" />
                                <input
                                  type="tel"
                                  id="mobilePhone"
                                  name="mobilePhone"
                                  value={formData.mobilePhone || formData.contact || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData((prev) => ({
                                      ...prev,
                                      mobilePhone: val,
                                      contact: val,
                                    }));
                                    if (formErrors.mobilePhone) {
                                      setFormErrors((prev) => ({
                                        ...prev,
                                        mobilePhone: "",
                                      }));
                                    }
                                  }}
                                  placeholder="98765 43210"
                                  className="exh-embed-mobile-input"
                                />
                              </div>
                            </div>
                            {formErrors.mobilePhone && (
                              <span className="exh-error-msg">
                                {formErrors.mobilePhone}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 10. Country */}
                        {showCountry && (
                          <div className="exh-field-group">
                            <label htmlFor="country" className="exh-label">
                              Country <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaGlobe className="exh-field-icon" />
                              <select
                                id="country"
                                name="country"
                                value={formData.country || ""}
                                onChange={handleInputChange}
                                disabled={get_country_loading}
                                className="exh-select"
                              >
                                <option value="">
                                  {get_country_loading
                                    ? "Loading Countries..."
                                    : "Select Country"}
                                </option>
                                {get_country_data &&
                                  get_country_data.map((c) => (
                                    <option
                                      key={c.id || c.country_name || c.name}
                                      value={c.id || c.country_name || c.name}
                                    >
                                      {c.name || c.country_name || c.title}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            {formErrors.country && (
                              <span className="exh-error-msg">
                                {formErrors.country}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 11. State */}
                        {showState && (
                          <div className="exh-field-group">
                            <label htmlFor="state" className="exh-label">
                              State <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaLocationDot className="exh-field-icon" />
                              <select
                                id="state"
                                name="state"
                                value={formData.state || ""}
                                onChange={handleInputChange}
                                disabled={get_state_loading || (showCountry && !formData.country)}
                                className="exh-select"
                              >
                                <option value="">
                                  {showCountry && !formData.country
                                    ? "Select Country First"
                                    : get_state_loading
                                      ? "Loading States..."
                                      : "Select State"}
                                </option>
                                {get_state_data &&
                                  get_state_data.map((st) => (
                                    <option key={st.id || st.name} value={st.id}>
                                      {st.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            {formErrors.state && (
                              <span className="exh-error-msg">
                                {formErrors.state}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 12. City Dropdown */}
                        {showCity && (
                          <div className="exh-field-group">
                            <label htmlFor="city" className="exh-label">
                              City <span className="exh-required">*</span>
                            </label>
                            <div className="exh-input-wrapper">
                              <FaCity className="exh-field-icon" />
                              <select
                                id="city"
                                name="city"
                                value={formData.city || ""}
                                onChange={handleInputChange}
                                disabled={!formData.state || get_city_loading}
                                className="exh-select"
                              >
                                <option value="">
                                  {!formData.state
                                    ? "Select State First"
                                    : get_city_loading
                                      ? "Loading Cities..."
                                      : "Select City"}
                                </option>
                                {formData.state &&
                                  get_city_data &&
                                  get_city_data.map((ct) => (
                                    <option
                                      key={ct.id || ct.name}
                                      value={ct.id || ct.name}
                                    >
                                      {ct.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            {formErrors.city && (
                              <span className="exh-error-msg">
                                {formErrors.city}
                              </span>
                            )}
                          </div>
                        )}

                        {/* 13. Product Category Checkboxes */}
                        {showProductCategory && (
                          <div className="exh-field-group exh-field-wide">
                            <label className="exh-label">
                              Product Category <span className="exh-required">*</span>
                            </label>
                            <div className="exh-category-checkbox-grid">
                              {productCategoryOpts.map((category, index) => {
                                const isChecked = selectedProductCategories.includes(category);
                                return (
                                  <label
                                    key={index}
                                    className={`exh-category-checkbox-item ${isChecked ? "selected" : ""}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleCategoryToggle(category)}
                                      className="exh-checkbox-input"
                                    />
                                    <span className="exh-checkbox-label">{category}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {(formErrors.selectedProductCategories || formErrors.productCategory) && (
                              <span className="exh-error-msg">
                                {formErrors.selectedProductCategories || formErrors.productCategory}
                              </span>
                            )}
                          </div>
                        )}

                        {/* --- DYNAMIC CHILD FIELDS FROM API --- */}
                        {activeFormat?.visitor_format_child &&
                          Array.isArray(activeFormat.visitor_format_child) &&
                          activeFormat.visitor_format_child
                            .filter(Boolean)
                            .map((child) => {
                              const fieldKey = `child_${child.id}`;
                              const options = getChildOptions(child);

                              return (
                                <div
                                  key={child.id}
                                  className={`exh-field-group ${child.type === "textarea" ||
                                    ((child.type === "checkbox" || child.type === "radio") && options.length > 3)
                                    ? "exh-field-wide"
                                    : ""
                                    }`}
                                >
                                  <label className="exh-label">
                                    {child.label}{" "}
                                    {isChildRequired(child) && (
                                      <span className="exh-required">*</span>
                                    )}
                                  </label>

                                  {/* DROPDOWN */}
                                  {child.type === "dropdown" && (
                                    <div className="exh-input-wrapper">
                                      {getFieldIcon(child.label)}
                                      <select
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        className="exh-select"
                                      >
                                        <option value="">
                                          Please choose {child.label}
                                        </option>
                                        {options.map((opt, i) => (
                                          <option key={i} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {/* TEXT */}
                                  {(child.type === "text" || !child.type) && (
                                    <div className="exh-input-wrapper">
                                      {getFieldIcon(child.label)}
                                      <input
                                        type="text"
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        placeholder={`Enter ${child.label.toLowerCase()}`}
                                        className="exh-input"
                                      />
                                    </div>
                                  )}

                                  {/* DATE / DATETIME */}
                                  {(child.type === "date" || child.type === "datetime") && (
                                    <div className="exh-input-wrapper">
                                      <FaCalendarDays className="exh-field-icon" />
                                      <input
                                        type="date"
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        className="exh-input"
                                      />
                                    </div>
                                  )}

                                  {/* TEXTAREA */}
                                  {child.type === "textarea" && (
                                    <textarea
                                      name={fieldKey}
                                      value={formData[fieldKey] || ""}
                                      onChange={handleInputChange}
                                      placeholder={`Enter ${child.label.toLowerCase()}`}
                                      rows={3}
                                      className="exh-textarea"
                                    />
                                  )}

                                  {/* RADIO */}
                                  {child.type === "radio" && (
                                    <div className="exh-radio-group">
                                      {options.map((opt, i) => (
                                        <label
                                          key={i}
                                          className="exh-radio-item"
                                        >
                                          <input
                                            type="radio"
                                            name={fieldKey}
                                            value={opt}
                                            checked={
                                              formData[fieldKey] === opt
                                            }
                                            onChange={handleInputChange}
                                          />
                                          <span>{opt}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}

                                  {/* CHECKBOX */}
                                  {child.type === "checkbox" && (
                                    <div className="exh-checkbox-group">
                                      {options.map((opt, i) => {
                                        const currentArr =
                                          formData[fieldKey] || [];
                                        const isChecked =
                                          currentArr.includes(opt);
                                        return (
                                          <label
                                            key={i}
                                            className="exh-checkbox-item"
                                          >
                                            <input
                                              type="checkbox"
                                              name={fieldKey}
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const checked =
                                                  e.target.checked;
                                                setFormData((prev) => {
                                                  const oldVals =
                                                    prev[fieldKey] || [];
                                                  const newVals = checked
                                                    ? [...oldVals, opt]
                                                    : oldVals.filter(
                                                      (v) => v !== opt
                                                    );
                                                  return {
                                                    ...prev,
                                                    [fieldKey]: newVals,
                                                  };
                                                });
                                              }}
                                            />
                                            <span>{opt}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* FILE / IMAGE */}
                                  {(child.type === "file" || child.type === "image") && (
                                    <div className="exh-file-input-wrapper">
                                      <input
                                        type="file"
                                        id={fieldKey}
                                        name={fieldKey}
                                        accept="image/*,.pdf"
                                        onChange={(e) => {
                                          const selectedFile = e.target.files[0];
                                          setFormData((prev) => ({
                                            ...prev,
                                            [fieldKey]: selectedFile || null,
                                          }));
                                          if (formErrors[fieldKey]) {
                                            setFormErrors((prev) => ({
                                              ...prev,
                                              [fieldKey]: "",
                                            }));
                                          }
                                        }}
                                        className="exh-file-native-input"
                                      />
                                      <button
                                        type="button"
                                        className="exh-btn-choose-file"
                                      >
                                        Choose File
                                      </button>
                                      <span className="exh-file-chosen-text">
                                        {formData[fieldKey]?.name
                                          ? formData[fieldKey].name
                                          : "No file chosen"}
                                      </span>
                                    </div>
                                  )}

                                  {formErrors[fieldKey] && (
                                    <span className="exh-error-msg">
                                      {formErrors[fieldKey]}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                      </div>
                    </div>

                    {/* --- YOUR BUSINESS SECTOR --- */}
                    {showBusinessSector && (
                      <div className="exh-field-group exh-field-wide">
                        <label className="exh-label">
                          Your Business Sector <span className="exh-required">*</span>
                        </label>
                        <div className="exh-category-checkbox-grid">
                          {businessSectorOpts.map((sector, index) => {
                            const isChecked = selectedSectors.includes(sector);
                            return (
                              <label
                                key={index}
                                className={`exh-category-checkbox-item ${isChecked ? "selected" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleSectorToggle(sector)}
                                  className="exh-checkbox-input"
                                />
                                <span className="exh-checkbox-label">{sector}</span>
                              </label>
                            );
                          })}
                        </div>
                        {formErrors.selectedSectors && (
                          <span className="exh-error-msg">
                            {formErrors.selectedSectors}
                          </span>
                        )}
                      </div>
                    )}

                    {/* --- WHERE DID YOU HEAR ABOUT US? --- */}
                    {showHeardAbout && (
                      <div
                        className="exh-sector-card-container"
                        style={{
                          border: "1px solid #cbd5e1",
                          borderRadius: "12px",
                          padding: "16px 20px",
                          background: "#ffffff",
                          marginTop: "16px",
                          marginBottom: "0px",
                        }}
                      >
                        <div className="exh-field-group" style={{ marginBottom: 0 }}>
                          <label htmlFor="heardAbout" className="exh-label">
                            Where did you hear about us?{" "}
                            <span className="exh-required">*</span>
                          </label>
                          <div className="exh-input-wrapper">
                            <FaGlobe className="exh-field-icon" />
                            {heardAboutOpts.length > 0 ? (
                              <select
                                id="heardAbout"
                                name="heardAbout"
                                value={formData.heardAbout || ""}
                                onChange={handleInputChange}
                                className="exh-select"
                              >
                                <option value="">Where did you hear about us?</option>
                                {heardAboutOpts.map((opt, idx) => (
                                  <option key={idx} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                id="heardAbout"
                                name="heardAbout"
                                value={formData.heardAbout || ""}
                                onChange={handleInputChange}
                                placeholder="Where did you hear about us?"
                                className="exh-input"
                              />
                            )}
                          </div>
                          {formErrors.heardAbout && (
                            <span className="exh-error-msg">
                              {formErrors.heardAbout}
                            </span>
                          )}

                          {/* Dynamic text field when "Other" option is selected */}
                          {(formData.heardAbout?.trim().toLowerCase() === "other" ||
                            formData.heardAbout?.trim().toLowerCase().includes("other")) && (
                              <div style={{ marginTop: "12px" }}>
                                <div className="exh-input-wrapper">
                                  <FaGlobe className="exh-field-icon" />
                                  <input
                                    type="text"
                                    id="heardAboutOther"
                                    name="heardAboutOther"
                                    value={formData.heardAboutOther || ""}
                                    onChange={handleInputChange}
                                    placeholder="Please specify where you heard about us..."
                                    className="exh-input"
                                  />
                                </div>
                                {formErrors.heardAboutOther && (
                                  <span className="exh-error-msg">
                                    {formErrors.heardAboutOther}
                                  </span>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {/* --- DYNAMIC CHILD FIELDS FROM API (RENDERED AFTER HEARD ABOUT US) --- */}
                    {activeFormat?.visitor_format_child &&
                      Array.isArray(activeFormat.visitor_format_child) &&
                      activeFormat.visitor_format_child.filter(Boolean).length > 0 && (
                        <div
                          className="exh-form-grid"
                          style={{ marginTop: "16px" }}
                        >
                          {activeFormat.visitor_format_child
                            .filter(Boolean)
                            .map((child) => {
                              const fieldKey = `child_${child.id}`;
                              const options = getChildOptions(child);

                              return (
                                <div
                                  key={child.id}
                                  className={`exh-field-group ${child.type === "textarea" ||
                                    ((child.type === "checkbox" || child.type === "radio") && options.length > 3)
                                    ? "exh-field-wide"
                                    : ""
                                    }`}
                                >
                                  <label className="exh-label">
                                    {child.label}{" "}
                                    {isChildRequired(child) && (
                                      <span className="exh-required">*</span>
                                    )}
                                  </label>

                                  {/* DROPDOWN */}
                                  {child.type === "dropdown" && (
                                    <div className="exh-input-wrapper">
                                      {getFieldIcon(child.label)}
                                      <select
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        className="exh-select"
                                      >
                                        <option value="">
                                          Please choose {child.label}
                                        </option>
                                        {options.map((opt, i) => (
                                          <option key={i} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {/* TEXT */}
                                  {(child.type === "text" || !child.type) && (
                                    <div className="exh-input-wrapper">
                                      {getFieldIcon(child.label)}
                                      <input
                                        type="text"
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        placeholder={`Enter ${child.label.toLowerCase()}`}
                                        className="exh-input"
                                      />
                                    </div>
                                  )}

                                  {/* DATE / DATETIME */}
                                  {(child.type === "date" || child.type === "datetime") && (
                                    <div className="exh-input-wrapper">
                                      <FaCalendarDays className="exh-field-icon" />
                                      <input
                                        type="date"
                                        name={fieldKey}
                                        value={formData[fieldKey] || ""}
                                        onChange={handleInputChange}
                                        className="exh-input"
                                      />
                                    </div>
                                  )}

                                  {/* TEXTAREA */}
                                  {child.type === "textarea" && (
                                    <textarea
                                      name={fieldKey}
                                      value={formData[fieldKey] || ""}
                                      onChange={handleInputChange}
                                      placeholder={`Enter ${child.label.toLowerCase()}`}
                                      rows={3}
                                      className="exh-textarea"
                                    />
                                  )}

                                  {/* RADIO */}
                                  {child.type === "radio" && (
                                    <div className="exh-radio-group">
                                      {options.map((opt, i) => (
                                        <label
                                          key={i}
                                          className="exh-radio-item"
                                        >
                                          <input
                                            type="radio"
                                            name={fieldKey}
                                            value={opt}
                                            checked={
                                              formData[fieldKey] === opt
                                            }
                                            onChange={handleInputChange}
                                          />
                                          <span>{opt}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}

                                  {/* CHECKBOX */}
                                  {child.type === "checkbox" && (
                                    <div className="exh-checkbox-group">
                                      {options.map((opt, i) => {
                                        const currentArr =
                                          formData[fieldKey] || [];
                                        const isChecked =
                                          currentArr.includes(opt);
                                        return (
                                          <label
                                            key={i}
                                            className="exh-checkbox-item"
                                          >
                                            <input
                                              type="checkbox"
                                              name={fieldKey}
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const checked =
                                                  e.target.checked;
                                                setFormData((prev) => {
                                                  const oldVals =
                                                    prev[fieldKey] || [];
                                                  const newVals = checked
                                                    ? [...oldVals, opt]
                                                    : oldVals.filter(
                                                      (v) => v !== opt
                                                    );
                                                  return {
                                                    ...prev,
                                                    [fieldKey]: newVals,
                                                  };
                                                });
                                              }}
                                            />
                                            <span>{opt}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* FILE / IMAGE */}
                                  {(child.type === "file" || child.type === "image") && (
                                    <div className="exh-file-input-wrapper">
                                      <input
                                        type="file"
                                        id={fieldKey}
                                        name={fieldKey}
                                        accept="image/*,.pdf"
                                        onChange={(e) => {
                                          const selectedFile = e.target.files[0];
                                          setFormData((prev) => ({
                                            ...prev,
                                            [fieldKey]: selectedFile || null,
                                          }));
                                          if (formErrors[fieldKey]) {
                                            setFormErrors((prev) => ({
                                              ...prev,
                                              [fieldKey]: "",
                                            }));
                                          }
                                        }}
                                        className="exh-file-native-input"
                                      />
                                      <button
                                        type="button"
                                        className="exh-btn-choose-file"
                                      >
                                        Choose File
                                      </button>
                                      <span className="exh-file-chosen-text">
                                        {formData[fieldKey]?.name
                                          ? formData[fieldKey].name
                                          : "No file chosen"}
                                      </span>
                                    </div>
                                  )}

                                  {formErrors[fieldKey] && (
                                    <span className="exh-error-msg">
                                      {formErrors[fieldKey]}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}

                    {/* SUBMIT BUTTON */}
                    <div className="exh-form-actions">
                      <button
                        type="submit"
                        disabled={submit_manufacturer_exhibitor_loading}
                        className="exh-btn-submit exh-btn-animated"
                      >
                        <span className="exh-btn-shine" />
                        {submit_manufacturer_exhibitor_loading ? (
                          <span className="exh-btn-loading">
                            <span className="exh-spinner" /> Submitting...
                          </span>
                        ) : (
                          <>
                            <span>SUBMIT</span>
                            <FaArrowRight className="exh-btn-arrow" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Right Sidebar Column */}
              <div className="exh-sidebar-col">
                {/* Exhibitor Benefits Card */}
                {register_detail_expo_data?.registration_detail
                  ?.why_registers &&
                  register_detail_expo_data?.registration_detail?.why_registers
                    .length > 0 && (
                    <div className="exh-sidebar-card">
                      <div className="exh-sb-header">
                        <FaGem className="exh-sb-icon" />
                        <h3>Exhibitor Benefits</h3>
                      </div>

                      <div className="exh-perks-list">
                        {register_detail_expo_data?.registration_detail?.why_registers
                          .filter((item) => item.name || item.description)
                          .map((item, index) => (
                            <div
                              className="exh-perk-item"
                              key={item.id || index}
                            >
                              <div className="exh-perk-bullet">
                                <FaCircleCheck />
                              </div>
                              <div>
                                {item.name && <h4>{item.name}</h4>}
                                {item.description && <p>{item.description}</p>}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Exhibitor Support Desk */}
                <div className="exh-sidebar-card exh-help-card">
                  <div className="exh-sb-header">
                    <FaHeadset className="exh-sb-icon exh-sb-icon-orange" />
                    <h3>Exhibitor Helpdesk</h3>
                  </div>
                  {(register_detail_expo_data?.registration_detail
                    ?.registration_heplpdesk_description ||
                    register_detail_expo_data?.registration_heplpdesk_description) && (
                      <p className="exh-help-text">
                        {register_detail_expo_data?.registration_detail
                          ?.registration_heplpdesk_description ||
                          register_detail_expo_data?.registration_heplpdesk_description}
                      </p>
                    )}

                  {register_detail_expo_data?.registration_detail
                    ?.registration_helpdesks &&
                    register_detail_expo_data?.registration_detail
                      ?.registration_helpdesks.length > 0 && (
                      <div className="exh-contact-methods">
                        {register_detail_expo_data?.registration_detail?.registration_helpdesks
                          .filter((item) => item.title || item.detail)
                          .map((item, index) => (
                            <div
                              className="exh-contact-row"
                              key={item.id || index}
                            >
                              <div className="exh-c-icon">
                                <FaHeadset />
                              </div>
                              <div>
                                {item.title && (
                                  <span className="exh-c-lbl">
                                    {item.title}
                                  </span>
                                )}
                                {item.detail && (
                                  <span className="exh-c-val">
                                    {item.detail}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                </div>

                {/* Security Box */}
                <div
                  className="exh-sec-box"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "16px",
                    padding: "14px 18px",
                    borderRadius: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontSize: "0.85rem",
                    color: "#64748b",
                  }}
                >
                  <FaShieldHalved
                    style={{
                      fontSize: "1.2rem",
                      color: "#0f172a",
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    Your details are 100% confidential and secure with Bharat
                    Buildcon.
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default SubmitManufacturer;
